import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewEncapsulation,
} from '@angular/core';
import { Subject, takeUntil, tap } from 'rxjs';
import { DOCUMENT } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { CompanyService } from '@cp/shared/data-access/company/company.service';
import { HeaderService } from '../../services/header.service';
import { ProgressMeterService } from '../../services/progress-meter.service';
import { PwaService } from '../../services/pwa.service';

@Component({
  selector: 'cp-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnInit, OnDestroy {
  private _destroy$ = new Subject<void>();

  public isSticky = false;
  public modalsActive = false;
  public isProgressMeterActive = false;
  public logoSvgId = this._companyService.getConfigEntry('headerSvgId');
  public isPwaBanner$ = this._pwaService.isPwaBanner$;

  constructor(
    private _progressMeterService: ProgressMeterService,
    private _cdRef: ChangeDetectorRef,
    private _headerService: HeaderService,
    private _renderer2: Renderer2,
    private _dialog: MatDialog,
    private _companyService: CompanyService,
    @Inject(DOCUMENT) private _document: Document,
    private _pwaService: PwaService,
  ) {}

  @HostListener('window:scroll')
  public onScroll(): void {
    this._handleStickyHeader();
  }

  @HostListener('window:resize')
  public onResize(): void {
    if (window.innerWidth > 970) {
      this._headerService.closeNavigation(this._renderer2, document);
    }
    this._handleStickyHeader();
  }

  public ngOnInit(): void {
    this._progressMeterService.meter$
      .pipe(
        tap((status) => {
          this.isProgressMeterActive = status;
          this._cdRef.detectChanges();
        }),
        takeUntil(this._destroy$),
      )
      .subscribe();

    this._dialog.afterOpened
      .pipe(
        tap(() => {
          this.modalsActive = true;
          this._handleStickyHeader();
          this._cdRef.detectChanges();
        }),
      )
      .subscribe();

    this._dialog.afterAllClosed
      .pipe(
        tap(() => {
          this.modalsActive = false;
          this._handleStickyHeader();
          this._cdRef.detectChanges();
        }),
      )
      .subscribe();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public onBannerClose(): void {
    this._pwaService.dismissBanner();
  }

  public async onInstallClicked(): Promise<void> {
    await this._pwaService.onInstall();
  }

  private _isScrolledByCdk(): boolean {
    const htmlElement = this._document.querySelector('html');

    if (!htmlElement) {
      return false;
    }

    const hasClass = htmlElement.classList.contains('cdk-global-scrollblock');

    if (hasClass) {
      const top = htmlElement.style.top.replace('px', '');
      const topNumber = Number(top);

      return topNumber < -100;
    }

    return false;
  }

  private _handleStickyHeader(): void {
    if (window.innerWidth > 970) {
      if (window.scrollY > 120) {
        if (!this.isSticky) {
          this.isSticky = true;
        }
      } else {
        if (this.isSticky && !this._isScrolledByCdk()) {
          this.isSticky = false;
        }
      }
    }
  }
}
